import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";

interface WaitlistModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  featureTitle?: string;
}

export default function WaitlistModal({ open, onOpenChange, featureTitle }: WaitlistModalProps) {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    city: "",
    permission: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const isFormComplete = useMemo(() => {
    return formData.firstName.trim() !== "" && 
           formData.lastName.trim() !== "" && 
           formData.email.trim() !== "" && 
           formData.city.trim() !== "" && 
           formData.permission;
  }, [formData]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePermissionChange = (checked: boolean) => {
    setFormData(prev => ({ ...prev, permission: checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isFormComplete) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields and accept the permission checkbox.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const webhookData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        city: formData.city,
        permission: formData.permission,
        timestamp: new Date().toISOString(),
        source: "House Beat Waitlist"
      };

      const response = await fetch("https://hook.us2.make.com/67fwtc2ougcffkvr4vc3mu56pif2fcb2", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(webhookData),
      });

      if (response.ok) {
        onOpenChange(false);
        navigate("/waitlist/success");
      } else {
        throw new Error("Failed to submit form");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Something went wrong",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            {featureTitle ? (
              <div className="space-y-2">
                <div>
                  We'll be ready to help you
                </div>
                <div>
                  <span className="text-brand-blue">{featureTitle}</span> soon.
                </div>
                <div>
                  Join the waitlist.
                </div>
              </div>
            ) : (
              "Join the Waitlist"
            )}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          <div className="space-y-4">
            <div>
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => handleInputChange("firstName", e.target.value)}
                placeholder="Enter your first name"
                className="mt-1.5"
                required
              />
            </div>

            <div>
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => handleInputChange("lastName", e.target.value)}
                placeholder="Enter your last name"
                className="mt-1.5"
                required
              />
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder="Enter your email"
                className="mt-1.5"
                required
              />
            </div>

            <div>
              <Label htmlFor="city">City *</Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => handleInputChange("city", e.target.value)}
                placeholder="Enter your city"
                className="mt-1.5"
                required
              />
            </div>

            <div className="flex items-start space-x-3 pt-2">
              <Checkbox
                id="permission"
                checked={formData.permission}
                onCheckedChange={handlePermissionChange}
                className="mt-0.5"
              />
              <Label 
                htmlFor="permission" 
                className="text-sm leading-relaxed cursor-pointer"
              >
                I give House Beat permission to contact me about updates and early access opportunities. *
              </Label>
            </div>
          </div>

          <Button
            type="submit"
            variant="brand"
            className="w-full font-bold"
            disabled={!isFormComplete || isSubmitting}
          >
            {isSubmitting ? "Joining..." : "Join the Waitlist"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
